import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import {
	IconButton,
	Checkbox,
	FormControlLabel,
	Grid,
	Fab,
	Tooltip,
	Typography,
} from '@mui/material';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ToastsContainer, ToastsStore, ToastsContainerPosition } from 'react-toasts';
import DeleteIcon from '@mui/icons-material/Delete';
import routes from '../../constants/Routes';
import CreateTwoToneIcon from '@mui/icons-material/CreateTwoTone';
import VisibilityTwoToneIcon from '@mui/icons-material/VisibilityTwoTone';
import { MultiSelect } from 'primereact/multiselect';
import AddIcon from '@mui/icons-material/Add';
import clsx from 'clsx';
import { InputText } from 'primereact/inputtext';
import { deviceTypesList } from 'store/actions/deviceTypeActions';
import { resetTemplatesByDeviceType } from 'store/actions/templateActions';
import classNames from 'classnames';
import { useAppDispatch, useAppSelector } from 'Stores/Hooks/hooks';
import { resetPopup, showPopup } from 'Stores/Slices/popUpSlice';
import { deleteAdvertisementById, getAdvertisements, resetAdvertisementForm, updateAdvertisementMode ,getAdvertisementDetailsById, AdvertisementState} from 'Stores/Slices/advertisementSlice';
import { RootState } from 'Stores/store';
import { useNavigate } from 'react-router-dom';
const PREFIX = 'AdvertisingList';

const classes = {
    fab: `${PREFIX}-fab`,
    absolute: `${PREFIX}-absolute`,
    fabIcon: `${PREFIX}-fabIcon`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`& .${classes.fab}`]: {
		margin: theme.spacing(2),
	},

    [`& .${classes.absolute}`]: {},

    [`& .${classes.fabIcon}`]: {
		color: theme.palette.common.white,
		backgroundColor: "#042630",
		'&:hover': {
			backgroundColor: "rgba(4, 38, 48, 0.8)",
		},
	}
}));

/**
 * Landing component showing Table with list of all advertisements with action
 * buttons.
 */
const AdvertisingList = () => {
	const [tableRows, setTableRows] = useState(10);
	const [loading, setLoading] = useState(false);
	const [first, setFirst] = useState(0);

	const dispatch = useAppDispatch();
  const navigate = useNavigate();
	const advertisementStore: AdvertisementState = useAppSelector(
		(state: RootState) => state.advertisement,
	);
	const {
		selectedId,
		list: advertisementList,
		totalElements: totalRecords,
	} = advertisementStore;
	const popup = useAppSelector((state: RootState) => state.popup);
	const { confirmation } = popup;
	useEffect(() => {
		if (confirmation === 'agree') {
			dispatch(resetPopup());
			dispatch(deleteAdvertisementById(selectedId ? selectedId : 0));
		}
	}, [confirmation, dispatch, selectedId]);
	const [page, setPage] = useState(0);
	useEffect(() => {
		dispatch(getAdvertisements());
	}, [dispatch]);

	const [sortField, setSortField] = useState('');
	const [sortOrder, setSortOrder] = useState(1);
	const sortMap = new Map([['deviceType.deviceType', 'deviceType']]);
	const getSortRequestArray = (
		inputSortField: any = sortField,
		inputSortOrder: any = sortOrder,
	) => {
		const sortArray: any = [];
		if (inputSortField && inputSortField.length) {
			const object = {
				columnName: sortMap.get(inputSortField)
					? sortMap.get(inputSortField)
					: inputSortField,
				direction: inputSortOrder === -1 ? 'desc' : 'asc',
			};
			sortArray.push(object);
		}

		return sortArray;
	};

	const [isFilterHidden, setIsFilterHidden] = useState(false);
	const [globalFilter] = useState('');

	const filterMap = new Map();
	const mapRef = useRef(filterMap);

	const transformStatusToCSSClass = (status: any) => {
		return status.replace(/\s/g, '').toLowerCase();
	};
	const AdvertisementStatus = (rowData: any) => {
		return (
            (<Root>
                {rowData.status ? (
					<>
						<span
							className={classNames(
								'campaign-status',
								'status-' + transformStatusToCSSClass(rowData.status),
							)}>
							{rowData.status}
						</span>
					</>
				) : (
					<> </>
				)}
            </Root>)
        );
	};

	useEffect(() => {
		const filterNames = ['name', 'status', 'clientName'];
		filterNames.forEach((filterName) => {
			mapRef.current.set(filterName, '');
		});
	}, []);
	const getFilterRequestArray = () => {
		const requestFilter = [];
		for (const [key, value] of mapRef.current) {
			const requestFilterObject: any = {};
			requestFilterObject.columnName = key;
			requestFilterObject.value = value;
			requestFilterObject.operationType = 'MATCH';
			requestFilter.push(requestFilterObject);
		}

		return requestFilter;
	};

	const generateTextFilter = (filterName: any) => (
		<InputText
			onInput={(e: any) => {
				filterMap.set(filterName, e.target.value);
				const requestObject = getFilterRequestArray();
				dispatch(getAdvertisements(page, tableRows, requestObject));
			}}
			className="p-column-filter"
		/>
	);

	const deviceType = (rowData: any) => {
		return (
			<>
				<Typography>{rowData.deviceType.deviceType}</Typography>
			</>
		);
	};



	const [columns] = useState([
		{
			field: 'name',
			header: 'Advertisement Name',
			filterElement: generateTextFilter('name'),
			filterPlaceHolderText: 'Search by Advertisement Name',
			sortable: true,
		},
		{
			field: 'templateName',
			header: 'Template Name',
			filterElement: generateTextFilter('templateName'),
			filterPlaceHolderText: 'Search by Advertisement Name',
			sortable: true,
		},
		{
			field: 'deviceType.deviceType',
			header: 'Device Type',
			sortable: false,
			body: deviceType,
		},
		{
			field: 'status',
			header: 'Status',
			body: AdvertisementStatus,
			sortable: true,
			filterElement: generateTextFilter('status'),
			filterPlaceHolderText: 'Search by Advertisement Name',
		},
	]);
	const [selectedColumns, setselectedColumns] = useState<any>(columns);

	const onColumnToggle = (e: any) => {
		const selectedColumns = e.value;
		const orderedSelectedColumns: any = columns.filter((col) =>
			selectedColumns.includes(col),
		);
		setselectedColumns(orderedSelectedColumns);
	};
	const columnComponents = selectedColumns?.map((col: any) => {
		return (
			<Column
				key={col.field}
				field={col.field}
				body={col.body}
				header={col.header}
				sortable={col.sortable}
				filter={isFilterHidden}
				filterPlaceholder={col.filterPlaceHolderText}
				filterElement={col.filterElement}
				filterMatchMode="custom"
			/>
		);
	});

	const header = (
		<>
			<div>
				<span style={{ float: 'left' }}>
					<MultiSelect
						value={selectedColumns}
						options={columns}
						optionLabel="header"
						onChange={(e) => onColumnToggle(e)}
						style={{ width: '350px', border: 'none' }}
					/>
				</span>
				<div style={{ textAlign: 'right' }}>
					<FormControlLabel
						control={
							<Checkbox
								name="showFilters"
                style={{ color: '#042630' }}
								onChange={(e) => onFiltersShow(e.target.checked)}
							/>
						}
						label="Show Filters"
					/>
				</div>
			</div>
		</>
	);

	const openViewModal = (rowData: any) => {
		dispatch(updateAdvertisementMode(rowData.id, 'VIEW'));
		dispatch(resetAdvertisementForm());
		dispatch(getAdvertisementDetailsById(rowData.id));
    navigate(routes.VIEW_ADVERTISEMENT_DETAILS.path)
	};

	const openCreateClientModal = () => {
		dispatch(updateAdvertisementMode(0, 'CREATE'));
		dispatch(deviceTypesList());
		dispatch(resetTemplatesByDeviceType());
    navigate(routes.CREATE_ADVERTISEMENT_DETAILS.path)
	};

	const openDeleteModal = (rowData: any) => {
		dispatch(updateAdvertisementMode(rowData.id, 'DELETE'));
		dispatch(showPopup('Are you sure that you want to delete?'));
	};

	const openEditModal = (rowData: any) => {
		dispatch(updateAdvertisementMode(rowData.id, 'EDIT'));
		dispatch(resetAdvertisementForm());
		dispatch(getAdvertisementDetailsById(rowData.id));

		dispatch(deviceTypesList());
    navigate(routes.EDIT_ADVERTISEMENT_DETAILS.path)
	};

	const onFiltersShow = (isFilterShown: any) => {
		setIsFilterHidden(isFilterShown);
	};

	const actionTemplate = (rowData: any, column: any) => {
		return (
            <div>
				<IconButton
                    aria-label="view"
                    onClick={() => {
						openViewModal(rowData);
					}}
                    size="large">
					<VisibilityTwoToneIcon fontSize="small" />
				</IconButton>
				<IconButton
                    aria-label="edit"
                    onClick={() => {
						openEditModal(rowData);
					}}
                    size="large">
					<CreateTwoToneIcon fontSize="small" />
				</IconButton>

				<IconButton
                    aria-label="delete"
                    onClick={() => {
						openDeleteModal(rowData);
					}}
                    size="large">
					<DeleteIcon fontSize="small" />
				</IconButton>
			</div>
        );
	};



	const onPage = async (event: any) => {
		setLoading(true);
		try {
			const { first: _first, rows } = event;
			dispatch(
				getAdvertisements(
					event.page,
					rows,
					getFilterRequestArray(),
					getSortRequestArray(),
				),
			);
			setPage(event.page);
			setFirst(_first);
			setLoading(false);
			setTableRows(rows);
		} catch (error) {
			console.error('Error:', error);
			setLoading(false);
		}
	};

	return <>
        <Grid style={{padding:"20px"}}>
            <ToastsContainer
                className="toast"
                store={ToastsStore}
                position={ToastsContainerPosition.TOP_CENTER}
            />
            <Grid container justifyContent="space-between">
                <Grid item>
                    {/* <InputText
                        type="search"
                        placeholder="Global Search"
                        onInput={(e) => setGlobalFilter(e.currentTarget.value)}
                        style={{ width: '500px', margin: '10px' }}
                    /> */}
                </Grid>
                <Grid item>
                    <Tooltip title="Create Advertisement" aria-label="add">
                        <Fab
                            size="medium"
                            className={clsx(classes.absolute, classes.fabIcon)}
                            onClick={openCreateClientModal}>
                            <AddIcon fontSize="small" />
                        </Fab>
                    </Tooltip>
                </Grid>
            </Grid>

            <div style={{ backgroundColor: '#DDEEF6', margin: '10px' }}>
                <div className="content-section implementation">
                    <DataTable
                        resizableColumns
                        columnResizeMode="expand"
                        reorderableColumns={true}
                        value={advertisementList}
                        globalFilter={globalFilter}
                        emptyMessage="No records found"
                        header={header}
                        paginator={true}
                        sortField={sortField}
                        sortOrder={sortOrder === 0
                            ? 0
                            : sortOrder === 1
                            ? 1
                            : sortOrder === -1
                            ? -1
                            : null}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink
                        LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                        rows={10}
                        lazy
                        onSort={(e) => {
                            setSortField(e.sortField);
                            setSortOrder(e.sortOrder ? e.sortOrder : 1);

                            dispatch(
                                getAdvertisements(
                                    page,
                                    tableRows,
                                    getFilterRequestArray(),
                                    getSortRequestArray(e.sortField, e.sortOrder),
                                ),
                            );
                        }}
                        first={first}
                        onPage={onPage}
                        loading={loading}
                        totalRecords={totalRecords}
                        rowsPerPageOptions={[5, 10, 20]}
                        selectionMode="single">
                        {columnComponents}
                        {/* <Column
                            field="name"
                            header="Advertisement Name"
                            sortable={true}
                            filter={isFilterHidden}
                        />
                        <Column
                            field="templateName"
                            header="Template Name"
                            sortable={true}
                            filter={isFilterHidden}
                        />
                        <Column
                            field="status"
                            header="Status"
                            sortable={true}
                            filter={isFilterHidden}
                        />

                        <Column
                            field="duration"
                            header="Duration"
                            sortable={true}
                            filter={isFilterHidden}
                        /> */}

                        <Column
                            body={actionTemplate}
                            style={{ textAlign: 'center', width: '12em' }}
                            header="Actions"
                            field=""
                            reorderable={false}
                            sortable={false}
                        />
                    </DataTable>
                </div>
            </div>
        </Grid>
    </>;
};

export default AdvertisingList;
